import { motion, useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import { Icon } from '../components/Icon'
import { ShapeGlyph } from '../components/Shape'
import type { Shape } from '@clicklinx/protocol'
import { useCountdown } from '../lib/useCountdown'

/**
 * The five-second answer window.
 *
 * This is a custom keypad rather than a focused `<input>` on purpose: on a
 * phone, the system keyboard takes several hundred milliseconds to animate in
 * and then covers half the screen. Out of a five-second budget that is not a
 * polish issue, it is the difference between answering and timing out. Physical
 * keyboards still work for desktop players.
 */
export function AnswerPad({
  deadlineLocal,
  targetShape,
  targetLabel,
  totalAcrossScreens = false,
  onSubmit,
}: {
  deadlineLocal: number
  targetShape: Shape | null
  targetLabel: string
  /** Scatter asks for a running total, not the count on one screen. */
  totalAcrossScreens?: boolean
  onSubmit: (value: number) => void
}) {
  const reduceMotion = useReducedMotion()
  const remaining = useCountdown(deadlineLocal)
  const [value, setValue] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const totalMs = 5_000
  const fraction = Math.max(0, Math.min(1, remaining / totalMs))
  const urgent = remaining <= 2_000

  const submit = useCallback(() => {
    if (submitted || value.length === 0) return
    setSubmitted(true)
    onSubmit(Number(value))
  }, [submitted, value, onSubmit])

  const press = useCallback(
    (digit: string) => {
      if (submitted) return
      // Three digits is well past any round this game generates.
      setValue((current) => (current.length >= 3 ? current : current + digit))
    },
    [submitted],
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (submitted) return
      if (/^[0-9]$/.test(event.key)) press(event.key)
      else if (event.key === 'Backspace') setValue((v) => v.slice(0, -1))
      else if (event.key === 'Enter') submit()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [press, submit, submitted])

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center gap-5">
      <div className="flex items-center gap-2.5 text-fg-muted">
        {targetShape && (
          <span className="text-lime">
            <ShapeGlyph shape={targetShape} size={22} />
          </span>
        )}
        <p className="text-sm">
          {totalAcrossScreens ? (
            <>
              Total <span className="font-semibold text-fg">{targetLabel}</span> across all screens?
            </>
          ) : (
            <>
              How many <span className="font-semibold text-fg">{targetLabel}</span>?
            </>
          )}
        </p>
      </div>

      {/* Value + time bar */}
      <div className="w-full max-w-sm">
        <div
          className={`relative flex h-24 items-center justify-center rounded-2xl border-2 transition-colors duration-200 ${
            submitted
              ? 'border-lime/60 bg-lime-wash'
              : urgent
                ? 'border-danger/60 bg-danger/8'
                : 'border-ink-700 bg-ink-950/70'
          }`}
        >
          <span
            className={`font-mono text-5xl font-bold tnum ${
              value ? (submitted ? 'text-lime' : 'text-fg') : 'text-fg-dim/40'
            }`}
          >
            {value || '—'}
          </span>
          {submitted && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="absolute right-4 text-lime"
            >
              <Icon name="check" size={22} />
            </motion.span>
          )}
        </div>

        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink-800" role="timer">
          <motion.div
            className="h-full rounded-full"
            style={{
              width: `${fraction * 100}%`,
              background: urgent ? 'var(--color-danger)' : 'var(--color-lime)',
            }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.06, ease: 'linear' }}
          />
        </div>
        <p className="mt-2 text-center font-mono text-xs tnum text-fg-dim">
          {(remaining / 1000).toFixed(1)}s
        </p>
      </div>

      {/* Keypad */}
      <div className="grid w-full max-w-sm grid-cols-3 gap-2.5">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <KeypadButton key={digit} onPress={() => press(digit)} disabled={submitted}>
            {digit}
          </KeypadButton>
        ))}

        <KeypadButton
          onPress={() => setValue((v) => v.slice(0, -1))}
          disabled={submitted || value.length === 0}
          tone="muted"
          label="Delete last digit"
        >
          <Icon name="arrowLeft" size={20} />
        </KeypadButton>

        <KeypadButton onPress={() => press('0')} disabled={submitted}>
          0
        </KeypadButton>

        <KeypadButton
          onPress={submit}
          disabled={submitted || value.length === 0}
          tone="primary"
          label="Submit answer"
        >
          <Icon name="check" size={22} />
        </KeypadButton>
      </div>
    </div>
  )
}

function KeypadButton({
  children,
  onPress,
  disabled,
  tone = 'default',
  label,
}: {
  children: React.ReactNode
  onPress: () => void
  disabled?: boolean
  tone?: 'default' | 'primary' | 'muted'
  label?: string
}) {
  const reduceMotion = useReducedMotion()
  const tones = {
    default: 'bg-ink-850 border-ink-700 text-fg hover:border-ink-600 active:bg-ink-800',
    primary: 'bg-lime border-lime text-ink-950 hover:brightness-110',
    muted: 'bg-ink-900 border-ink-700 text-fg-muted hover:text-fg',
  }[tone]

  return (
    <motion.button
      type="button"
      aria-label={label}
      // pointerdown, not click: on a five-second clock the ~80ms that click
      // waits for a potential double-tap is worth removing.
      onPointerDown={disabled ? undefined : onPress}
      disabled={disabled}
      whileTap={disabled || reduceMotion ? undefined : { scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 800, damping: 32 }}
      className={`no-tap-delay flex h-[3.75rem] items-center justify-center rounded-xl border font-mono text-2xl font-semibold tnum transition-colors duration-150 disabled:opacity-30 disabled:pointer-events-none ${tones}`}
    >
      {children}
    </motion.button>
  )
}
